/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components/ServerNetworkBox.tsx
 ??? Created: 2026-07-22
 ?? Description:
    Network and VPN interface configuration for Telephony Servers with i18n support.
----------------------------------------------------------------*/

import { Network } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';

type FormData = Record<string, any>;

interface Props {
    formData: FormData;
    errors: Record<string, string>;
    onInputChange: (field: string, value: unknown) => void;
}

const INPUT_CLS = 'px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent';

export function ServerNetworkBox({ formData, errors, onInputChange }: Props) {
    const { t } = useI18n();
    const { isActive: debug } = useDebugMode();

    return (
        <FormBox title={t('telephonyServers.sections.networkInfo')} icon={<Network className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* Bind Interface */}
                <div className="space-y-1">
                    <SimpleTooltip content={t('telephonyServers.tooltips.bindInterface')} position="top" maxWidth="300px">
                        <label className="text-sm font-medium text-foreground">
                            {t('telephonyServers.bindInterface')}
                            {debug && <span className="block text-xs text-muted-foreground font-mono">bind_interface</span>}
                        </label>
                    </SimpleTooltip>
                </div>
                <select
                    value={formData.bindInterface || 'auto'}
                    onChange={(e) => onInputChange('bindInterface', e.target.value)}
                    className={`${INPUT_CLS} border-border`}
                >
                    <option value="auto">{t('telephonyServers.options.bindInterface.auto')}</option>
                    <option value="wg0">{t('telephonyServers.options.bindInterface.wg0')}</option>
                    <option value="tun0">{t('telephonyServers.options.bindInterface.tun0')}</option>
                    <option value="custom">{t('telephonyServers.options.bindInterface.custom')}</option>
                </select>

                {/* Enable VPN */}
                <div className="space-y-1">
                    <SimpleTooltip content={t('telephonyServers.tooltips.useVpn')} position="top" maxWidth="300px">
                        <label className="text-sm font-medium text-foreground">
                            {t('telephonyServers.useVpn')}
                            {debug && <span className="block text-xs text-muted-foreground font-mono">use_vpn</span>}
                        </label>
                    </SimpleTooltip>
                </div>
                <div className="flex items-center">
                    <Toggle value={formData.useVpn ?? false} onChange={(v) => onInputChange('useVpn', v)} label="" />
                </div>

                {/* VPN IP */}
                {(formData.useVpn || formData.bindInterface === 'custom') && (
                    <>
                        <div className="space-y-1">
                            <SimpleTooltip content={t('telephonyServers.tooltips.vpnIp')} position="top" maxWidth="300px">
                                <label className="text-sm font-medium text-foreground">
                                    {t('telephonyServers.vpnIp')}
                                    {debug && <span className="block text-xs text-muted-foreground font-mono">vpn_ip</span>}
                                </label>
                            </SimpleTooltip>
                        </div>
                        <input
                            type="text"
                            value={formData.vpnIp || ''}
                            onChange={(e) => onInputChange('vpnIp', e.target.value)}
                            className={`${INPUT_CLS} ${errors.vpnIp ? 'border-red-500' : 'border-border'}`}
                            placeholder={t('telephonyServers.placeholders.vpnIp')}
                        />
                    </>
                )}
            </div>
        </FormBox>
    );
}
